import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import Text from 'tio-ui/components/text';
import { PhCheckCircle, PhWarningCircle, PhX } from 'ember-phosphor-icons';
import ServicerAnswerForm from 'tio-employee/components/slr/resolution-alert/servicer-answer-form';
import type { ResolutionAlert, ServicerOption } from 'tio-employee/utils/slr/resolution-alert';
import type MemberResolutionsService from 'tio-employee/services/member-resolutions';
import type { MemberAnswer } from 'tio-employee/services/member-resolutions';
import type { IntlService } from 'ember-intl';

/** Where the host is mounted; it sets the chrome, the copy, and whether a dismiss can show. */
export type ResolutionAlertSurface = 'dashboard' | 'documents' | 'drawer';

type AnswerFailure = 'stale' | 'closed' | 'failed';

function answerFailure(error: unknown): AnswerFailure {
  const status =
    (error as { status?: number })?.status ??
    (error as { response?: { status?: number } })?.response?.status;
  if (status === 409) return 'stale';
  if (status === 404) return 'closed';
  return 'failed';
}

const SURFACE_CLASSES: Record<ResolutionAlertSurface, string> = {
  dashboard: 'tio-col px-4 py-6 w-full',
  documents: 'tio-col p-4 w-full',
  drawer: 'tio-col p-3 w-full border-l-4',
};

interface ResolutionAlertSignature {
  Args: {
    /** The server-authored question, off the alert feed or a reflection broadcast. */
    alert: ResolutionAlert;
    /** The page mounting the card; it picks the chrome and copy, and a dismiss on `documents` never shows. */
    surface: ResolutionAlertSurface;
    /** Fires once the answer is recorded; by then every surface already shows the confirmation. */
    onAnswered?: (answer: MemberAnswer) => void;
    /** Fires after the member dismisses a temporary alert; the question itself stays open. */
    onDismiss?: () => void;
  };
  Element: HTMLDivElement;
}

/**
 * The member resolution-alert host: renders a pending question by its `resolutionType`
 * — the servicer pick for `dropdown` — and owns what every variant shares: the answer's
 * failure copy, the in-place confirmation card for the page session, and a dismiss on
 * temporary alerts that hides the row and stores nothing. A type it has no control for
 * still shows the server's copy.
 */
export default class ResolutionAlertHost extends Component<ResolutionAlertSignature> {
  @service('member-resolutions') declare memberResolutions: MemberResolutionsService;
  @service declare intl: IntlService;

  @tracked failure: AnswerFailure | null = null;
  @tracked hiddenInDrawer = false;

  get answer() {
    return this.memberResolutions.answerFor(this.args.alert);
  }

  get isHidden() {
    if (this.args.surface === 'drawer') return this.hiddenInDrawer;
    return this.args.surface === 'dashboard' && this.memberResolutions.isDismissed(this.args.alert);
  }

  get canDismiss() {
    return (
      this.args.surface !== 'documents' &&
      this.args.alert.persistence === 'temporary' &&
      !this.answer
    );
  }

  get showsForm() {
    return !this.answer && this.failure !== 'stale' && this.failure !== 'closed';
  }

  get state() {
    if (this.answer) return 'confirmed';
    if (this.submit.isRunning) return 'confirming';
    return this.failure ? `failed-${this.failure}` : 'unanswered';
  }

  get frameClass() {
    const tone = this.args.surface === 'drawer' ? this.toneBorder : '';
    return `${SURFACE_CLASSES[this.args.surface]} ${tone}`.trim();
  }

  get toneBorder() {
    return this.answer ? 'tio-border-success' : 'tio-border-warning';
  }

  get testHookPrefix() {
    return `resolution-alert-${this.args.surface}`;
  }

  // The drawer and the temporary dashboard card split the headline in two, as their designs do.
  get usesShortCopy() {
    return (
      this.args.surface === 'drawer' ||
      (this.args.surface === 'dashboard' && this.args.alert.persistence === 'temporary')
    );
  }

  get title() {
    if (this.answer) return this.#confirmedCopy('title');
    return this.#alertCopy('title', this.args.alert.title);
  }

  get description() {
    if (this.answer) return this.#confirmedCopy('desc');
    return this.#alertCopy('desc', this.args.alert.description);
  }

  get failureMessage() {
    return this.failure ? this.intl.t(this.#typeKey(`error_${this.failure}`)) : '';
  }

  // A variant's copy lives under its resolution type, so a new variant adds keys, not host branches.
  #typeKey(suffix: string) {
    return `slr.reimagined.resolution_alert.${this.args.alert.resolutionType}.${suffix}`;
  }

  #alertCopy(part: 'title' | 'desc', serverCopy: string) {
    const base = `slr.reimagined.alert_${this.args.alert.type.replace(/-/g, '_')}`;
    const keys = this.usesShortCopy
      ? [`${base}_short_${part}`, `${base}_${part}`]
      : [`${base}_${part}`];
    const key = keys.find((candidate) => this.intl.exists(candidate));
    return key ? this.intl.t(key) : serverCopy;
  }

  #confirmedCopy(part: 'title' | 'desc') {
    const surface = this.args.surface === 'drawer' ? 'drawer_' : '';
    return this.intl.t(this.#typeKey(`${surface}confirmed_${part}`), {
      servicer: this.answer?.servicer.name ?? '',
    });
  }

  dismiss = () => {
    if (this.args.surface === 'drawer') {
      this.hiddenInDrawer = true;
    } else {
      this.memberResolutions.dismiss(this.args.alert);
    }
    this.args.onDismiss?.();
  };

  submit = dropTask(async (servicer: ServicerOption) => {
    this.failure = null;
    try {
      const answer = await this.memberResolutions.answerServicer(this.args.alert, servicer);
      this.args.onAnswered?.(answer);
    } catch (error) {
      this.failure = answerFailure(error);
    }
  });

  <template>
    {{#unless this.isHidden}}
      <Card
        @elevation="none"
        @padding="none"
        class={{this.frameClass}}
        data-test-resolution-alert
        data-test-resolution-alert-surface={{@surface}}
        data-test-resolution-alert-state={{this.state}}
        ...attributes
      >
        <div class="tio-row-top w-full">
          <div
            class="tio-row-center rounded-lg shrink-0 size-10
              {{if this.answer 'tio-bg-success-soft' 'tio-bg-warning-soft'}}"
          >
            {{#if this.answer}}
              <PhCheckCircle @size={{24}} class="tio-fg-success" />
            {{else}}
              <PhWarningCircle @size={{24}} class="tio-fg-warning" />
            {{/if}}
          </div>
          <div class="tio-col-extra-tight tio-fill-min">
            <Text @weight="semibold" data-test-resolution-alert-title>{{this.title}}</Text>
            <Text @variant="muted" data-test-resolution-alert-description>
              {{this.description}}
            </Text>
          </div>
          {{#if this.canDismiss}}
            <Button
              @appearance="minimal"
              @size="sm"
              @icon={{PhX}}
              @ariaLabel={{t "slr.reimagined.resolution_alert.dismiss"}}
              @testHook="{{this.testHookPrefix}}__dismiss"
              data-test-resolution-alert-dismiss
              {{on "click" this.dismiss}}
            />
          {{/if}}
        </div>
        {{#if this.showsForm}}
          {{#if (eq @alert.resolutionType "dropdown")}}
            <ServicerAnswerForm
              @alert={{@alert}}
              @testHookPrefix={{this.testHookPrefix}}
              @isSubmitting={{this.submit.isRunning}}
              @onConfirm={{this.submit.perform}}
            />
          {{/if}}
        {{/if}}
        {{#if this.failure}}
          <Text
            @size="sm"
            @variant="error"
            role="alert"
            data-test-resolution-alert-error={{this.failure}}
          >
            {{this.failureMessage}}
          </Text>
        {{/if}}
      </Card>
    {{/unless}}
  </template>
}
